import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { memo, RefObject, useCallback, useLayoutEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import cx from "classnames";

import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";
import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { OVERSCAN, ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import useTableSimpleEffectiveColumnPinning from "ds/components/TableSimple/hooks/useEffectiveColumnPinning";
import TableSimpleRow from "ds/components/TableSimple/Row";
import { TableCellPerColumn, TableSimpleStore } from "ds/components/TableSimple/types";
import { IntentResource } from "types/generated";

import useIntentProjectResourcesHierarchyStore from "./useResourcesHierarchyStore";
import { selectFlatList, IntentResourceRow } from "./tableDataSelectors";
import IntentProjectResourcesTableServiceRow from "./ServiceRow";
import IntentProjectResourcesTableStickyGroupRow from "./StickyGroupRow";
import styles from "./styles.module.css";

const SERVICE_ROW_HEIGHT_ESTIMATE = 96;

type IntentProjectResourcesTableRowsProps = {
  data: IntentResource[];
  store: TableSimpleStore;
  cellPerColumn: TableCellPerColumn<IntentResourceRow>;
  tableRef: RefObject<HTMLDivElement | null>;
};

const IntentProjectResourcesTableRows = ({
  data,
  store,
  cellPerColumn,
  tableRef,
}: IntentProjectResourcesTableRowsProps) => {
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  const { childrenByGroup, groupMeta } = useIntentProjectResourcesHierarchyStore(
    useShallow((state) => ({
      childrenByGroup: state.childrenByGroup,
      groupMeta: state.groupMeta,
    }))
  );

  const { rows, hasGroups, stickyRanges } = selectFlatList({ childrenByGroup, groupMeta }, data);

  const { rangeExtractor, stickyGroupsVisibleOnTableRef } = useStickyRangeExtractor(stickyRanges);

  const getItemKey = useCallback(
    (index: number) => {
      const row = rows[index];
      return "serviceType" in row ? row.id : row.resourceID;
    },
    [rows]
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableRef.current,
    estimateSize: useCallback(
      (index: number) =>
        "serviceType" in rows[index] ? SERVICE_ROW_HEIGHT_ESTIMATE : ROW_HEIGHT_ESTIMATE,
      [rows]
    ),
    overscan: OVERSCAN,
    getItemKey,
    rangeExtractor,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rows.length, virtualizer]);

  const virtualizedItems = virtualizer.getVirtualItems();
  const [topGap, bottomGap] = calculateVirtualPadding(
    virtualizedItems,
    virtualizer.getTotalSize(),
    virtualizer.options.scrollMargin,
    stickyGroupsVisibleOnTableRef.current.size
  );

  const renderRow = useCallback(
    (virtualRow: VirtualItem) => {
      const row = rows[virtualRow.index];

      if ("serviceType" in row) {
        return (
          <IntentProjectResourcesTableServiceRow
            key={virtualRow.key}
            row={row}
            columns={columns}
            isFirstColumnPinned={effectiveIsFirstColumnPinned}
            measureRef={virtualizer.measureElement}
            dataIndex={virtualRow.index}
          />
        );
      }

      if (row.isOpenGroup) {
        const stickyGroupEnd = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
        const isActiveSticky = stickyGroupEnd !== undefined;
        const nextAfter = isActiveSticky
          ? virtualizer.measurementsCache[stickyGroupEnd + 1]
          : undefined;
        const nextAfterSticky = nextAfter
          ? nextAfter.start - (virtualizer.measurementsCache[virtualRow.index]?.size ?? 0) + 1
          : undefined;

        return (
          <IntentProjectResourcesTableStickyGroupRow
            key={virtualRow.key}
            row={row}
            columns={columns}
            isFirstColumnPinned={effectiveIsFirstColumnPinned}
            cellPerColumn={cellPerColumn}
            keyboardProps={keyboardProps}
            measureRef={virtualizer.measureElement}
            dataIndex={virtualRow.index}
            isActiveSticky={isActiveSticky}
            nextAfterSticky={nextAfterSticky}
            tableRef={tableRef}
          />
        );
      }

      return (
        <TableSimpleRow
          key={virtualRow.key}
          itemId={row.resourceID}
          item={row}
          columns={columns}
          isFirstColumnPinned={effectiveIsFirstColumnPinned}
          cellPerColumn={cellPerColumn}
          keyboardProps={keyboardProps}
          measureRef={virtualizer.measureElement}
          dataIndex={virtualRow.index}
          className={cx(styles.row, {
            [styles.childRow]: row.isChild,
            [styles.alignParent]: hasGroups && !row.isChild && !row.isGroup,
          })}
          virtualized
        />
      );
    },
    [
      rows,
      hasGroups,
      columns,
      effectiveIsFirstColumnPinned,
      cellPerColumn,
      keyboardProps,
      tableRef,
      virtualizer.measureElement,
      virtualizer.measurementsCache,
      stickyGroupsVisibleOnTableRef,
    ]
  );

  return (
    <>
      {topGap > 0 && <div style={{ display: "table-row", height: topGap }} aria-hidden="true" />}
      {virtualizedItems.map(renderRow)}
      {bottomGap > 0 && (
        <div style={{ display: "table-row", height: bottomGap }} aria-hidden="true" />
      )}
    </>
  );
};

IntentProjectResourcesTableRows.displayName = "IntentProjectResourcesTableRows";

export default memo(IntentProjectResourcesTableRows);
